/**
 * The width each fee-schedule column starts at, and the narrowest it is drawn.
 *
 * The fee schedule tables are list tables: their columns say what they hold, and the shared fit in
 * `table-column-fit.ts` sizes them from their headings and content once they are drawn (#6708). These
 * are the widths before that: the first paint, and any renderer with no layout. The redline grid,
 * which is not fitted, still takes its minimums from here.
 *
 * The share-out that used to live here is the shared fit's now. Its rules came from this table: a
 * column is never narrower than its heading, the table scrolls rather than taking a column out (#5395),
 * and a description is capped rather than handed the slack (#5111).
 */

/**
 * A column's starting width, and the narrowest it can be and still name itself.
 *
 * A minimum is the heading's label, 32px of comfortable padding, and the 42px the column menu takes on
 * hover (#6545). Description's maximum is the cap #5111 set; both fee schedule tables hand it to the
 * shared fit as the column's own maximum, so spare room never widens a description past it.
 */
export type FeeScheduleColumnWidth = {
  preferred: number;
  minimum: number;
  maximum?: number;
};

export const FEE_SCHEDULE_COLUMN_WIDTHS: Readonly<Record<string, FeeScheduleColumnWidth>> = {
  locked: { preferred: 40, minimum: 40 },
  name: { preferred: 220, minimum: 120 },
  description: { preferred: 280, minimum: 140, maximum: 320 },
  fixedFee: { preferred: 110, minimum: 105 },
  fee: { preferred: 150, minimum: 95 },
  unit: { preferred: 100, minimum: 85 },
  teamMember: { preferred: 190, minimum: 115 },
  type: { preferred: 110, minimum: 95 },
  // Edit, Remove Category and the history menu: 32 + 32 + 40px of buttons and 8px of cell padding
  // (#5797). A row with fewer controls centres them in the same column.
  "mrt-row-actions": { preferred: 116, minimum: 112 },
};

/** What a column asks for when it is not one of the ones above — MRT's own default column width. */
const UNKNOWN_COLUMN_WIDTH: FeeScheduleColumnWidth = { preferred: 180, minimum: 140 };

export function feeScheduleColumnWidth(columnId: string): FeeScheduleColumnWidth {
  return FEE_SCHEDULE_COLUMN_WIDTHS[columnId] ?? UNKNOWN_COLUMN_WIDTH;
}
